<template>
    <div class="editor-container">
        <div class="wysiwyg-editor">
            <div class="toolbar">
                <div class="toolbar-group">
                    <button @click="execCommand('bold')" :class="{ active: isActive('bold') }" title="Negrito">
                        <strong>B</strong>
                    </button>
                    <button @click="execCommand('italic')" :class="{ active: isActive('italic') }" title="Itálico">
                        <i>I</i>
                    </button>
                    <button @click="execCommand('underline')" :class="{ active: isActive('underline') }" title="Sublinhado">
                        <u>U</u>
                    </button>
                </div>

                <div class="toolbar-group">
                    <select @change="execCommandWithArg('fontName', $event.target.value)" class="font-select">
                        <option value="Arial">Arial</option>
                        <option value="Times New Roman">Times New Roman</option>
                        <option value="Courier New">Courier New</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Verdana">Verdana</option>
                    </select>

                    <select @change="execCommandWithArg('fontSize', $event.target.value)" class="size-select">
                        <option value="1">Pequeno</option>
                        <option value="3">Normal</option>
                        <option value="4">Grande</option>
                        <option value="5">Maior</option>
                        <option value="7">Enorme</option>
                    </select>
                </div>

                <div class="toolbar-group">
                    <button @click="execCommand('justifyLeft')" :class="{ active: isActive('justifyLeft') }" title="Alinhar à Esquerda">⫷</button>
                    <button @click="execCommand('justifyCenter')" :class="{ active: isActive('justifyCenter') }" title="Centralizar">☰</button>
                    <button @click="execCommand('justifyRight')" :class="{ active: isActive('justifyRight') }" title="Alinhar à Direita">⫸</button>
                </div>

                <div class="toolbar-group">
                    <button @click="openVariableModal" title="Inserir Variável" class="var-button">Var</button>
                    <button @click="toggleSourceView" :class="{ active: showSource }" title="Ver HTML">
                        &lt;/&gt;
                    </button>
                </div>
            </div>

            <div
                v-show="!showSource"
                ref="editor"
                class="editor-content"
                contenteditable
                @input="onInput"
                @keydown="onKeyDown"
                @blur="onBlur"
                @focus="onFocus"
            ></div>

            <textarea
                v-show="showSource"
                v-model="editorContent"
                class="source-editor"
                @input="onSourceInput"
            >      </textarea>
        </div>

        <div
            v-if="showVariablesDropdown"
            class="variables-dropdown"
            :style="{ left: dropdownPosition.x + 'px', top: dropdownPosition.y + 'px' }"
        >
            <div
                v-for="variable in variables"
                :key="variable.name"
                class="dropdown-item"
                @click="insertVariable(variable.name)"
            >
                {{ variable.label }}
            </div>
        </div>

        <div v-if="showVariableModal" class="modal" @click.self="closeVariableModal">
            <div class="modal-content">
                <h3>Inserir Variável</h3>
                <div class="variables-list">
                    <button
                        v-for="variable in variables"
                        :key="variable.name"
                        @click="insertVariable(variable.name)"
                    >
                        {{ variable.label }}
                    </button>
                </div>
                <button class="close-btn" @click="closeVariableModal">Fechar</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'WysiwygEditor',

    data() {
        return {
            editorContent: '',
            showVariableModal: false,
            showVariablesDropdown: false,
            showSource: false,
            dropdownPosition: { x: 0, y: 0 },
            lastChar: '',
            variables: [
                { name: 'nome_cliente', label: 'Nome do Cliente' },
                { name: 'email', label: 'Email' },
                { name: 'data', label: 'Data' }
            ],
            selection: null,
            range: null
        }
    },

    mounted() {
        this.$refs.editor.innerHTML = this.editorContent
    },

    methods: {
        execCommand(command) {
            try {
                if (!this.hasSelection()) {
                    this.setEndOfContenteditable(this.$refs.editor)
                }
                document.execCommand(command, false)
                this.$refs.editor.focus()
            } catch (error) {
                console.error('Erro ao executar comando:', error)
            }
        },

        execCommandWithArg(command, value) {
            try {
                if (!this.hasSelection()) {
                    this.setEndOfContenteditable(this.$refs.editor)
                }
                document.execCommand(command, false, value)
                this.$refs.editor.focus()
            } catch (error) {
                console.error('Erro ao executar comando:', error)
            }
        },

        hasSelection() {
            const sel = window.getSelection()
            return sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed
        },

        setEndOfContenteditable(contentEditableElement) {
            const range = document.createRange()
            range.selectNodeContents(contentEditableElement)
            range.collapse(false)
            const selection = window.getSelection()
            selection.removeAllRanges()
            selection.addRange(range)
        },

        isActive(command) {
            return document.queryCommandState(command)
        },

        saveSelection() {
            if (window.getSelection) {
                const sel = window.getSelection()
                if (sel.getRangeAt && sel.rangeCount) {
                    this.range = sel.getRangeAt(0)
                }
            }
        },

        restoreSelection() {
            if (this.range) {
                const sel = window.getSelection()
                sel.removeAllRanges()
                sel.addRange(this.range)
            }
        },

        onInput(e) {
            this.editorContent = e.target.innerHTML
            this.$emit('input', this.editorContent)
            this.saveSelection()
        },

        onSourceInput(e) {
            this.editorContent = e.target.value
            this.$refs.editor.innerHTML = this.editorContent
            this.$emit('input', this.editorContent)
        },

        onKeyDown(e) {
            if (e.key === 'Tab') {
                e.preventDefault()
                document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;')
            } else if (e.key === '{') {
                const selection = window.getSelection()
                const position = selection.getRangeAt(0).startOffset
                const content = this.$refs.editor.textContent
                const lastChar = position > 0 ? content[position - 1] : ''

                if (lastChar === '{') {
                    e.preventDefault()
                    this.showVariablesDropdown = true
                    this.dropdownPosition = this.getCaretPosition()
                    // Remove o último '{'
                    document.execCommand('delete', false)
                }
            }
        },

        getCaretPosition() {
            const selection = window.getSelection()
            const range = selection.getRangeAt(0)
            const rect = range.getBoundingClientRect()
            return {
                x: rect.left,
                y: rect.bottom
            }
        },

        onFocus() {
            this.restoreSelection()
        },

        onBlur() {
            this.saveSelection()
        },

        toggleSourceView() {
            this.showSource = !this.showSource
        },

        openVariableModal() {
            this.saveSelection()
            this.showVariableModal = true
        },

        closeVariableModal() {
            this.showVariableModal = false
            this.$nextTick(() => {
                this.$refs.editor.focus()
                this.restoreSelection()
            })
        },

        insertVariable(varName) {
            this.restoreSelection()
            const variable = `{{${varName}}}`
            document.execCommand('insertText', false, variable)
            this.closeVariableModal();
            this.showVariablesDropdown = false
        },

        /*
        insertVariableFromDropdown(varName) {
            const variable = `{{${varName}}}`
            document.execCommand('insertText', false, variable)
            this.showVariablesDropdown = false
            this.$refs.editor.focus()
        }

         */
    }
}
</script>

<style scoped>
.editor-container {
    max-width: 1200px;
    margin: 0 auto;
}

.wysiwyg-editor {
    border: 1px solid #ccc;
    border-radius: 4px;
    background: white;
}

.toolbar {
    padding: 8px;
    border-bottom: 1px solid #eee;
    background: #f5f5f5;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.toolbar-group {
    display: flex;
    gap: 5px;
    align-items: center;
    padding-right: 10px;
    border-right: 1px solid #ddd;
}

.toolbar-group:last-child {
    border-right: none;
}

.font-select, .size-select {
    padding: 4px;
    border: 1px solid #ddd;
    border-radius: 3px;
    background: white;
    min-width: 120px;
}

.toolbar button {
    padding: 5px 10px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 3px;
    cursor: pointer;
    min-width: 30px;
}

.toolbar button.active {
    background: #e6e6e6;
    border-color: #999;
}

.var-button {
    background: #4a5568;
    color: white;
    border: none;
}

.var-button:hover {
    background: #2d3748;
}

.editor-content {
    min-height: 200px;
    padding: 10px;
    outline: none;
    overflow-y: auto;
}

.source-editor {
    width: 100%;
    min-height: 200px;
    padding: 10px;
    font-family: monospace;
    border: none;
    outline: none;
    resize: vertical;
}

.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 20px;
    border-radius: 4px;
    min-width: 300px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.variables-list {
    display: grid;
    gap: 8px;
    margin: 15px 0;
}

.variables-list button {
    padding: 8px;
    text-align: left;
    border: 1px solid #ddd;
    border-radius: 3px;
    background: white;
    cursor: pointer;
    transition: background-color 0.2s;
}

.variables-list button:hover {
    background: #f5f5f5;
}

.close-btn {
    width: 100%;
    padding: 8px;
    background: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 3px;
    cursor: pointer;
    margin-top: 10px;
}

.variables-dropdown {
    position: fixed;
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    z-index: 1000;
}

.dropdown-item {
    padding: 8px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
}

.dropdown-item:hover {
    background: #f5f5f5;
}
</style>
